import React, { useCallback } from "react";
import _ from "lodash";
import T from "prop-types";
import {
  CONDITION,
  CONSIGNMENT_TYPE,
  INVENTORIES,
  WARRANTY_OPTIONS
} from "MODULES/inventories/constants";
import {
  Check,
  CheckGroup,
  CurrencyField,
  FormInput,
  FormRow,
  NumberInput,
  Select,
  TextArea
} from "MODULES/shared/components/Forms";
import { Col, Collapse } from "react-bootstrap";
import { Field } from "redux-form";
import { arrayToOptions, toOptions } from "LIB/forms";
import { QUANTITY, RETURN_TO_FOB, RETURN_TO_TYPE } from "APP_ROOT/constants";
import { EMA } from "MODULES/Receiving/constants";
import {
  EXCHANGE_SALE,
  EXCHANGE_SPECIAL_INSTRUCTIONS,
  FLAT_EXCHANGE,
  REPAIR_FEE,
  SELLER_ID
} from "MODULES/SalesOrders/constants";
import { properCase } from "LIB/formatters/text";
import { WARRANTY } from "MODULES/OutgoingQuotes/constants";
import { TENANT_LOCATION } from "MODULES/shared/components/ShippingSection/shippingTypes";
import ExistingQuoteWarning from "MODULES/SalesOrders/components/ConvertFromCartForm/ExistingQuoteWarning";
import { fobFormat } from "LIB/formatters/address";
import { DO_NOT_EXCHANGE } from "MODULES/companies/constants";
import PartField from "MODULES/SalesOrders/ReserveForSale/ConvertDummyForm/PartField";
import PartFee from "./PartFee";
import { BlanketExchangeAgreementWarning } from "./Warnings";

export default function LineItemFields({
  canExchange,
  change,
  currentUser,
  fields,
  getField,
  selectedCustomer,
  selectedSeller,
  shipToAddress
}) {
  const changeExchange = useCallback((_ev, val) => {
    if (val) {
      change(RETURN_TO_TYPE, TENANT_LOCATION);
      if (shipToAddress) change(RETURN_TO_FOB, fobFormat(shipToAddress));
    } else {
      change("line_items[0].flat_exchange", val);
    }
  });

  return [
    <table className="table table-striped table-numbered margin-bottom-none">
      <tbody>
        {fields.map((item, idx) => {
          const hazmat = fields.get(idx).hazmat;
          const specialContainer = fields.get(idx).special_container_req;
          const exchange = fields.get(idx).exchange_sale;
          const quantity = Number(fields.get(idx).quantity);

          /* eslint-disable no-unused-vars */
          const {
            scrap,
            parts_unit,
            ...filteredConditions
          } = INVENTORIES.condition;
          /* eslint-enable no-unused-vars */

          return (
            <tr key={`item-${idx}`}>
              <td style={{ verticalAlign: "top", paddingTop: "12px" }}>
                {idx + 1}
              </td>
              <td>
                <table className="table">
                  <tbody>
                    <ExistingQuoteWarning
                      part={{}}
                      company={selectedCustomer}
                      sellerId={getField(SELLER_ID)}
                    />
                    <tr>
                      <td>
                        <FormRow>
                          <Col md={12}>
                            <Collapse
                              unmountOnExit
                              in={parseInt(quantity, 10) === 1}
                            >
                              <div>
                                <FormRow>
                                  <Col md={3}>
                                    <CheckGroup title="Exchange Sale" justified>
                                      <Field
                                        name={`${item}.exchange_sale`}
                                        component={Check}
                                        label={properCase(EXCHANGE_SALE)}
                                        disabled={
                                          !canExchange ||
                                          getField(
                                            `${item}.${CONSIGNMENT_TYPE}`
                                          ) === EMA ||
                                          _.get(
                                            selectedCustomer,
                                            DO_NOT_EXCHANGE
                                          )
                                        }
                                        onChange={changeExchange}
                                      />
                                    </CheckGroup>
                                  </Col>
                                  {exchange && (
                                    <div>
                                      <Col md={3}>
                                        <CheckGroup
                                          title={properCase(FLAT_EXCHANGE)}
                                          justified
                                        >
                                          <Field
                                            name={`${item}.${FLAT_EXCHANGE}`}
                                            component={Check}
                                            label={properCase(FLAT_EXCHANGE)}
                                          />
                                        </CheckGroup>
                                      </Col>
                                      <Col md={3}>
                                        <Field
                                          name={
                                            "repair_administration_charge_percentage"
                                          }
                                          component={FormInput}
                                          label={
                                            "Repair Administration Charge (%)"
                                          }
                                        />
                                      </Col>
                                    </div>
                                  )}
                                  <Col md={7}>
                                    <BlanketExchangeAgreementWarning
                                      company={selectedCustomer}
                                      exchangeSale={exchange}
                                    />
                                  </Col>
                                </FormRow>
                                <Collapse in={exchange} unmountOnExit>
                                  <div>
                                    <FormRow>
                                      <Col md={3}>
                                        <CurrencyField
                                          name={`${item}.exchange_fee`}
                                          label={
                                            getField(`${item}.${FLAT_EXCHANGE}`)
                                              ? "Flat Exchange Fee"
                                              : "Exchange Fee"
                                          }
                                          required
                                        />
                                      </Col>
                                      <Col md={3}>
                                        <CurrencyField
                                          name={`${item}.outright_value`}
                                          label="Outright Value"
                                          required
                                        />
                                      </Col>
                                      <Col md={2}>
                                        <Field
                                          name={`${item}.core_due_days`}
                                          label="Core Due Days"
                                          component={NumberInput}
                                          roundDigits={0}
                                        />
                                      </Col>
                                      <Col md={3}>
                                        <Field
                                          name={`${item}.${REPAIR_FEE}`}
                                          label="Avg Repair (NTX)"
                                          component={FormInput}
                                          required={getField(
                                            `${item}.${FLAT_EXCHANGE}`
                                          )}
                                        />
                                      </Col>
                                    </FormRow>
                                    <FormRow>
                                      <Col md={12}>
                                        <Field
                                          name={`${item}.${EXCHANGE_SPECIAL_INSTRUCTIONS}`}
                                          component={TextArea}
                                          maxLength={150}
                                          label={
                                            "Special Instructions (To be incl. on Exch. Agreement)"
                                          }
                                          rows="5"
                                        />
                                      </Col>
                                    </FormRow>
                                  </div>
                                </Collapse>
                              </div>
                            </Collapse>
                          </Col>
                        </FormRow>
                        <FormRow>
                          <PartField item={item} />
                          {!exchange && (
                            <Col md={2}>
                              <CurrencyField
                                name={`${item}.price_per_unit`}
                                label="Sales Ea."
                                required
                              />
                            </Col>
                          )}
                          <Col md={2}>
                            <Field
                              label="Qty"
                              name={`${item}.${QUANTITY}`}
                              component={FormInput}
                            />
                          </Col>
                          <Col md={2}>
                            <Field
                              label={properCase(CONDITION)}
                              name={`${item}.condition`}
                              component={Select}
                              required
                              options={toOptions(filteredConditions)}
                            />
                          </Col>
                        </FormRow>
                      </td>
                    </tr>
                    <PartFee
                      currentSeller={selectedSeller}
                      currentUser={currentUser}
                      item={item}
                      hazmat={hazmat}
                      specialContainer={specialContainer}
                      change={change}
                      getField={getField}
                      left={
                        <Col md={3}>
                          <Field
                            label="Days Lead Time"
                            name={`${item}.lead_time`}
                            component={FormInput}
                          />
                        </Col>
                      }
                    />
                    <tr>
                      <td>
                        <div>
                          <h5 className="h4">QC Pre-Approval Required</h5>
                          <FormRow>
                            <Col md={5}>
                              <CheckGroup
                                title="QC Pre-Approval Required"
                                justified
                              >
                                <Field
                                  name={`${item}.customer_qc_preapproval_required`}
                                  component={Check}
                                  label="Required"
                                />
                              </CheckGroup>
                            </Col>
                          </FormRow>
                        </div>
                        <div>
                          <h5 className="h4">Warranty</h5>
                          <FormRow>
                            <Col md={3}>
                              <Field
                                component={Select}
                                label={properCase(WARRANTY)}
                                name={`${item}.warranty`}
                                options={arrayToOptions(WARRANTY_OPTIONS)}
                              />
                            </Col>
                          </FormRow>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>,
    <FormRow>
      <Col mdOffset={9} md={3} style={{ paddingTop: 10 }}>
        <button
          type="button"
          onClick={() => fields.push({ quantity: 1 })}
          className="btn btn-primary btn-hollow"
          style={{ marginTop: 5, marginBottom: 15, float: "right" }}
        >
          <i className="fa fa-fw fa-plus" /> Add Part
        </button>
      </Col>
    </FormRow>
  ];
}

LineItemFields.propTypes = {
  canExchange: T.bool,
  change: T.func,
  disableCustomerCqcApproval: T.func,
  fields: T.shape({}),
  getField: T.func,
  selectedCustomer: T.shape({}),
  selectedSeller: T.shape({}),
  shipToAddress: T.shape({})
};



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/ReserveForSale/ConvertDummyForm/LineItemFields.jsx